import React, { useState, useEffect } from 'react';
    import { Link, useLocation, useNavigate } from 'react-router-dom';
    import { motion, AnimatePresence } from 'framer-motion';
    import { useAuth } from '../contexts/AuthContext';
    import { useApp } from '../contexts/AppContext';
    import SafeIcon from '../common/SafeIcon';
    import * as FiIcons from 'react-icons/fi';
    import QuotesCarousel from '../components/QuotesCarousel';
    import { Triangle, Circle, Square, Hexagon, GeometricBackground } from '../components/GeometricShapes';

    const {
      FiBookOpen,
      FiUsers,
      FiMessageCircle,
      FiShoppingBag,
      FiTrendingUp,
      FiClock,
      FiStar,
      FiAward,
      FiTarget,
      FiEye,
      FiInfo,
      FiHeart,
      FiPlay,
      FiChevronDown,
      FiChevronUp,
      FiCheckCircle,
      FiCalendar,
      FiBarChart2,
      FiActivity,
      FiZap,
      FiTrophy,
      FiMapPin,
      FiFlag,
      FiChevronRight,
      FiMenu,
      FiGrid,
      FiMoreHorizontal,
      FiArrowRight,
      FiSearch,
      FiX,
    } = FiIcons;

    const Home = () => {
      const { user } = useAuth();
      const { addNotification } = useApp();
      const navigate = useNavigate();
      const location = useLocation();
      const [activeTab, setActiveTab] = useState('about');
      const [showStats, setShowStats] = useState(false);
      const [activeStatsTab, setActiveStatsTab] = useState('overview');
      const [showQuickActions, setShowQuickActions] = useState(false);

      // Search state and logic
      const [searchQuery, setSearchQuery] = useState('');
      const [showSearchResults, setShowSearchResults] = useState(false);

      const searchableContent = [
        { title: 'Mathematics Quiz', type: 'quiz', path: '/quiz/mathematics', description: 'Test your math skills' },
        { title: 'Science Quiz', type: 'quiz', path: '/quiz/science', description: 'Explore scientific concepts' },
        { title: 'English Literature Quiz', type: 'quiz', path: '/quiz/english', description: 'Literature and grammar' },
        { title: 'History Quiz', type: 'quiz', path: '/quiz/history', description: 'Learn about the past' },
        { title: 'Geography Quiz', type: 'quiz', path: '/quiz/geography', description: 'Discover the world' },
        { title: 'Leadership Skills Quiz', type: 'quiz', path: '/quiz/leadership', description: 'Develop leadership abilities' },
        { title: 'Study Room', type: 'page', path: '/study-room', description: 'Access all quizzes and learning materials' },
        { title: 'Community', type: 'page', path: '/community', description: 'Connect with other learners' },
        { title: 'Study Groups', type: 'page', path: '/study-groups', description: 'Join collaborative learning groups' },
        { title: 'Chat Room', type: 'page', path: '/chat', description: 'Communicate with peers' },
        { title: 'Profile', type: 'page', path: '/profile', description: 'View and edit your profile' },
        { title: 'Settings', type: 'page', path: '/settings', description: 'Customize your experience' },
      ];

      useEffect(() => {
        const handleClickOutside = (event) => {
          if (!event.target.closest('.home-search-container')) {
            setShowSearchResults(false);
          }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
      }, []);

      const handleSearchChange = (e) => {
        const query = e.target.value;
        setSearchQuery(query);
        setShowSearchResults(query.length > 0);
      };

      const getFilteredSearchResults = () => {
        if (!searchQuery.trim()) return [];
        const query = searchQuery.toLowerCase();
        return searchableContent
          .filter(
            (item) =>
              item.title.toLowerCase().includes(query) ||
              item.description.toLowerCase().includes(query) ||
              item.type.toLowerCase().includes(query)
          )
          .slice(0, 5); // Limit results
      };

      const handleSearchSubmit = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
          const bestMatch = getFilteredSearchResults()[0];
          if (bestMatch) {
            navigate(bestMatch.path);
          } else {
            navigate(`/study-room?q=${searchQuery}`);
          }
          setShowSearchResults(false);
        }
      };

      const handleSearchResultClick = (result) => {
        navigate(result.path);
        setSearchQuery('');
        setShowSearchResults(false);
      };
      
      const getTypeColor = (type) => {
        switch (type) {
          case 'quiz': return 'text-geometric-blue bg-blue-50 dark:bg-blue-900/50 dark:text-blue-300';
          case 'page': return 'text-geometric-green bg-green-50 dark:bg-green-900/50 dark:text-green-300';
          case 'topic': return 'text-geometric-purple bg-purple-50 dark:bg-purple-900/50 dark:text-purple-300';
          default: return 'text-neutral-600 bg-neutral-50 dark:bg-neutral-700 dark:text-neutral-300';
        }
      };
      
      const searchResults = getFilteredSearchResults();

      // Enhanced statistics data with more detailed metrics
      const userStats = {
        totalPoints: user?.points || 1250,
        quizzesCompleted: 47,
        studyGroups: 5,
        loginStreak: 14,
        nextLevel: 'Advanced',
        pointsToNextLevel: 250,
        rank: 37,
        totalUsers: 1247,
        percentile: 97,
        weeklyProgress: [65, 70, 85, 72, 90, 78, 82],
        weeklyAverage: 77.4,
        weeklyImprovement: 12.5,
        badges: [
          { name: 'Quick Learner', icon: FiZap, earned: true, date: '2024-05-10' },
          { name: 'Quiz Master', icon: FiStar, earned: true, date: '2024-05-05' },
          { name: 'Team Player', icon: FiUsers, earned: true, date: '2024-04-28' },
          { name: 'Knowledge Seeker', icon: FiBookOpen, earned: false },
          { name: 'Perfect Score', icon: FiCheckCircle, earned: false },
        ],
        recentSubjects: [
          { name: 'Mathematics', progress: 75, quizzes: 12, total: 15, improvement: 8 },
          { name: 'Science', progress: 60, quizzes: 8, total: 12, improvement: 5 },
          { name: 'English', progress: 85, quizzes: 14, total: 15, improvement: 12 },
        ],
        goals: [
          { name: 'Complete 50 quizzes', progress: 94, current: 47, target: 50 },
          { name: 'Join 10 study groups', progress: 50, current: 5, target: 10 },
          { name: 'Earn 1500 points', progress: 83, current: 1250, target: 1500 },
        ],
      };

      const menuItems = [
        { id: 'about', icon: FiInfo, label: 'About Us', path: '/about', color: 'blue', description: 'Our team and mission' },
        { id: 'vision', icon: FiEye, label: 'Our Vision', path: '/vision', color: 'green', description: 'Where we\'re headed' },
        { id: 'mission', icon: FiTarget, label: 'Our Mission', path: '/mission', color: 'purple', description: 'What drives us' },
        { id: 'values', icon: FiHeart, label: 'Our Values', path: '/organization', color: 'yellow', description: 'What we believe' },
      ];

      const studySubjects = [
        { name: 'Mathematics', icon: FiTrendingUp, color: 'blue', path: '/quiz/mathematics' },
        { name: 'Science', icon: FiBookOpen, color: 'green', path: '/quiz/science' },
        { name: 'English', icon: FiMessageCircle, color: 'purple', path: '/quiz/english' },
        { name: 'History', icon: FiClock, color: 'orange', path: '/quiz/history' },
        { name: 'Geography', icon: FiTarget, color: 'blue', path: '/quiz/geography' },
        { name: 'Leadership', icon: FiStar, color: 'yellow', path: '/quiz/leadership' },
        { name: 'Puzzles & Brain Teasers', icon: FiZap, color: 'pink', path: '/quiz/puzzles' },
      ];

      const quickActions = [
        { name: 'Study Room', icon: FiBookOpen, color: 'blue', path: '/study-room' },
        { name: 'Community', icon: FiUsers, color: 'green', path: '/community' },
        { name: 'Study Groups', icon: FiUsers, color: 'purple', path: '/study-groups' },
        { name: 'Chat', icon: FiMessageCircle, color: 'orange', path: '/chat' },
        { name: 'Marketplace', icon: FiShoppingBag, color: 'blue', path: '/marketplace' },
        { name: 'Profile', icon: FiUsers, color: 'yellow', path: '/profile' },
      ];

      // Close dropdown when clicking outside
      useEffect(() => {
        const handleClickOutside = (event) => {
          const dropdown = document.getElementById('quick-actions-dropdown');
          if (dropdown && !dropdown.contains(event.target) && !event.target.closest('.quick-actions-toggle')) {
            setShowQuickActions(false);
          }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
          document.removeEventListener('mousedown', handleClickOutside);
        };
      }, []);

      const toggleStats = () => {
        setShowStats(!showStats);
      };

      const toggleQuickActions = () => {
        setShowQuickActions(!showQuickActions);
      };

      return (
        <div className="space-y-6 px-4 py-4">
          {/* Main Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="relative home-search-container"
          >
            <form onSubmit={handleSearchSubmit} className="relative">
              <SafeIcon
                icon={FiSearch}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-neutral-400 dark:text-neutral-500 w-5 h-5"
              />
              <input
                type="text"
                placeholder="Search for anything..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="w-full h-14 pl-12 pr-4 py-2 border-2 border-neutral-200 dark:border-neutral-700 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base placeholder-neutral-500 dark:placeholder-neutral-400 bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm shadow-geometric-lg text-neutral-800 dark:text-neutral-200"
              />
            </form>
            {showSearchResults && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-neutral-800 rounded-2xl shadow-geometric-xl border-2 border-neutral-200 dark:border-neutral-700 z-50 max-h-80 overflow-y-auto backdrop-blur-sm">
                {searchResults.length > 0 ? (
                  <div className="py-2">
                    {searchResults.map((result, index) => (
                      <button
                        key={index}
                        onClick={() => handleSearchResultClick(result)}
                        className="w-full text-left px-4 py-3 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors border-b border-neutral-100 dark:border-neutral-700/50 last:border-b-0 group"
                      >
                        <div className="flex items-center space-x-3">
                          <div
                            className={`flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center ${getTypeColor(
                              result.type
                            )} group-hover:scale-105 transition-transform`}
                          >
                            <SafeIcon icon={FiSearch} className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-neutral-800 dark:text-neutral-200 group-hover:text-neutral-900 dark:group-hover:text-white text-ellipsis">
                              {result.title}
                            </p>
                            <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-1 line-clamp-1">{result.description}</p>
                          </div>
                          <SafeIcon icon={FiArrowRight} className="w-4 h-4 text-neutral-400 group-hover:text-primary-500 transition-transform group-hover:translate-x-1" />
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="px-4 py-6 text-center">
                    <SafeIcon icon={FiSearch} className="w-8 h-8 mx-auto text-neutral-300 dark:text-neutral-600 mb-2" />
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 text-wrap">No results for "{searchQuery}"</p>
                  </div>
                )}
              </div>
            )}
          </motion.div>

          {/* Quotes Carousel */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <QuotesCarousel />
          </motion.div>

          {/* Welcome Section - Geometric Design */}
          <GeometricBackground pattern="dots" className="rounded-2xl overflow-hidden">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/90 backdrop-blur-sm border border-neutral-200 shadow-geometric-lg overflow-hidden"
            >
              {/* Header - Always visible */}
              <div className="p-4 cursor-pointer transition-all hover:bg-neutral-50/50" onClick={toggleStats}>
                <div className="text-center relative">
                  {/* Geometric decorations */}
                  <div className="absolute top-0 left-2">
                    <Triangle size="sm" color="blue" rotation={15} className="opacity-20" />
                  </div>
                  <div className="absolute top-1 right-4">
                    <Circle size="sm" color="green" className="opacity-20" />
                  </div>
                  <div className="absolute bottom-0 right-2">
                    <Square size="sm" color="purple" rotation={45} className="opacity-20" />
                  </div>
                  <div className="relative z-10">
                    <h1 className="text-xl font-bold text-neutral-800 mb-2 flex items-center justify-center flex-wrap">
                      <div className="mr-2 p-2 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg shadow-geometric flex-shrink-0">
                        <SafeIcon icon={FiActivity} className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-wrap">Welcome back, {user?.name || 'Student'}!</span>
                    </h1>
                    <div className="flex items-center justify-center space-x-2 text-sm text-neutral-600 flex-wrap">
                      <span className="flex items-center text-center">
                        <span className="text-wrap">View your progress & statistics</span>
                        <span className="inline-flex items-center ml-2 px-2 py-1 bg-primary-50 text-primary-700 text-xs rounded-lg border border-primary-200 flex-shrink-0">
                          <SafeIcon icon={showStats ? FiChevronUp : FiChevronDown} className="w-3 h-3 mr-1" />
                          {showStats ? 'Hide' : 'Show'}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Enhanced Stats Section */}
              <AnimatePresence>
                {showStats && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="border-t border-neutral-200"
                  >
                    <div className="p-4 bg-gradient-to-br from-neutral-50 to-white">
                      {/* Stats Navigation Tabs */}
                      <div className="overflow-x-auto hide-scrollbar mb-4">
                        <div className="flex space-x-2 bg-white rounded-xl p-1 shadow-inner-geometric min-w-max">
                          {[
                            { id: 'overview', label: 'Overview', icon: FiActivity },
                            { id: 'subjects', label: 'Subjects', icon: FiBookOpen },
                            { id: 'achievements', label: 'Achievements', icon: FiAward },
                            { id: 'goals', label: 'Goals', icon: FiFlag },
                          ].map((tab) => (
                            <button
                              key={tab.id}
                              onClick={() => setActiveStatsTab(tab.id)}
                              className={`flex items-center space-x-1 py-2 px-3 text-xs font-medium rounded-lg transition-all whitespace-nowrap ${
                                activeStatsTab === tab.id
                                  ? 'bg-primary-500 text-white shadow-geometric'
                                  : 'text-neutral-600 hover:bg-neutral-100'
                              }`}
                            >
                              <SafeIcon icon={tab.icon} className="w-3 h-3" />
                              <span className="nav-text">{tab.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Overview Tab */}
                      {activeStatsTab === 'overview' && (
                        <div className="space-y-4">
                          {/* Summary Stats - Geometric Cards */}
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                            {[
                              { icon: FiAward, value: userStats.totalPoints, label: 'Total Points', color: 'primary' },
                              { icon: FiCheckCircle, value: userStats.quizzesCompleted, label: 'Quizzes', color: 'green' },
                              { icon: FiUsers, value: userStats.studyGroups, label: 'Groups', color: 'blue' },
                              { icon: FiCalendar, value: userStats.loginStreak, label: 'Day Streak', color: 'orange' },
                            ].map((stat, index) => (
                              <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="bg-white rounded-xl p-3 shadow-geometric border border-neutral-200 relative overflow-hidden group hover:shadow-geometric-lg transition-shadow"
                              >
                                {/* Geometric decoration */}
                                <div className="absolute top-1 right-1 opacity-10 group-hover:opacity-20 transition-opacity">
                                  <Hexagon size="md" color={stat.color} />
                                </div>
                                <div className="relative z-10">
                                  <div className={`p-2 rounded-lg bg-geometric-${stat.color}/10 w-fit mb-2`}>
                                    <SafeIcon icon={stat.icon} className={`w-4 h-4 text-geometric-${stat.color}`} />
                                  </div>
                                  <div className="text-lg font-bold text-neutral-800">{stat.value}</div>
                                  <div className="text-xs text-neutral-600 text-ellipsis">{stat.label}</div>
                                </div>
                              </motion.div>
                            ))}
                          </div>

                          {/* Level Progress - Geometric Design */}
                          <div className="bg-white rounded-xl p-4 shadow-geometric border border-neutral-200">
                            <div className="flex justify-between items-center mb-3">
                              <div className="flex items-center min-w-0 flex-1">
                                <SafeIcon icon={FiTarget} className="w-4 h-4 mr-2 text-primary-600 flex-shrink-0" />
                                <div className="text-sm font-semibold text-neutral-700 text-ellipsis">
                                  Next Level: {userStats.nextLevel}
                                </div>
                              </div>
                              <div className="text-xs text-neutral-500 bg-primary-50 px-2 py-1 rounded-lg border border-primary-200 flex-shrink-0">
                                {userStats.pointsToNextLevel} to go
                              </div>
                            </div>
                            <div className="w-full bg-neutral-200 rounded-lg h-2 mb-2 overflow-hidden">
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${Math.min(100, ((userStats.totalPoints % 1000) / 1000) * 100)}%` }}
                                transition={{ duration: 1, ease: 'easeOut' }}
                                className="bg-gradient-to-r from-primary-500 to-primary-600 h-full rounded-lg relative"
                              >
                                <div className="absolute inset-0 bg-white/30 animate-pulse"></div>
                              </motion.div>
                            </div>
                            <div className="flex justify-between text-xs text-neutral-500">
                              <div>Current: {userStats.totalPoints}</div>
                              <div>Target: {userStats.totalPoints + userStats.pointsToNextLevel}</div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Subjects Tab */}
                      {activeStatsTab === 'subjects' && (
                        <div className="space-y-4">
                          <div className="bg-white rounded-xl p-4 shadow-geometric border border-neutral-200">
                            <h3 className="text-sm font-semibold text-neutral-700 mb-3 flex items-center">
                              <SafeIcon icon={FiBookOpen} className="w-4 h-4 mr-2 text-primary-600" />
                              Subject Progress
                            </h3>
                            <div className="space-y-3">
                              {userStats.recentSubjects.map((subject, index) => (
                                <div key={index} className="border-b border-neutral-100 pb-3 last:border-0 last:pb-0">
                                  <div className="flex justify-between items-center mb-2">
                                    <div className="flex items-center min-w-0 flex-1">
                                      <div
                                        className={`w-2 h-2 rounded-sm ${
                                          index === 0
                                            ? 'bg-geometric-blue'
                                            : index === 1
                                            ? 'bg-geometric-green'
                                            : 'bg-geometric-purple'
                                        } mr-2 flex-shrink-0`}
                                      ></div>
                                      <span className="font-medium text-neutral-700 text-sm text-ellipsis">{subject.name}</span>
                                    </div>
                                    <div className="flex items-center space-x-2 flex-shrink-0">
                                      <span className="text-xs text-neutral-500">{subject.progress}%</span>
                                      <div className="flex items-center bg-green-50 px-1 py-0.5 rounded text-green-700 text-xs">
                                        <SafeIcon icon={FiTrendingUp} className="w-2 h-2 mr-1" />
                                        <span>+{subject.improvement}%</span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="w-full bg-neutral-200 rounded-lg h-1 mb-1">
                                    <div
                                      className={`h-full rounded-lg ${
                                        index === 0
                                          ? 'bg-geometric-blue'
                                          : index === 1
                                          ? 'bg-geometric-green'
                                          : 'bg-geometric-purple'
                                      }`}
                                      style={{ width: `${subject.progress}%` }}
                                    ></div>
                                  </div>
                                  <div className="text-xs text-neutral-500">
                                    {subject.quizzes} of {subject.total} quizzes completed
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          <Link
                            to="/study-room"
                            className="block w-full bg-white py-2 text-center text-xs font-medium text-primary-600 rounded-xl border border-primary-200 hover:bg-primary-50 transition-colors shadow-geometric"
                          >
                            View All Subjects
                          </Link>
                        </div>
                      )}

                      {/* Footer with View Profile Link */}
                      <div className="mt-4 text-center">
                        <Link
                          to="/profile"
                          className="inline-flex items-center text-primary-600 text-xs font-medium hover:text-primary-700 bg-primary-50 px-3 py-2 rounded-lg border border-primary-200 hover:bg-primary-100 transition-colors"
                        >
                          <span className="text-ellipsis">View Full Profile</span>
                          <SafeIcon icon={FiChevronRight} className="w-3 h-3 ml-1 flex-shrink-0" />
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </GeometricBackground>

          {/* About ILM Global Section - Geometric Design */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl shadow-geometric-lg p-4 border border-neutral-200 relative overflow-hidden"
          >
            {/* Geometric decorations */}
            <div className="absolute top-4 right-4">
              <Triangle size="lg" color="blue" rotation={30} className="opacity-5" />
            </div>
            <div className="absolute bottom-4 left-4">
              <Square size="md" color="green" rotation={45} className="opacity-5" />
            </div>
            <div className="text-center mb-4 relative z-10">
              <h2 className="text-xl font-bold text-neutral-800 mb-2 text-wrap"> About ILM Global </h2>
              <p className="text-neutral-600 mb-4 text-sm text-wrap">
                Learn about our organization, team, and the mission that drives us forward
              </p>
              {/* Enhanced Image with geometric frame */}
              <div className="relative rounded-2xl overflow-hidden mb-4 mx-auto max-w-sm shadow-geometric-lg">
                <img
                  src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1752594597944-PHOTO-2019-03-01-05-24-20%202.jpg"
                  alt="ILM Global Team"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                  <p className="font-medium text-sm text-wrap">Empowering the next generation of leaders</p>
                </div>
                {/* Geometric frame elements */}
                <div className="absolute top-2 left-2">
                  <Circle size="sm" color="primary" className="text-white/50" />
                </div>
                <div className="absolute top-2 right-2">
                  <Square size="sm" color="primary" rotation={45} className="text-white/50" />
                </div>
              </div>
            </div>
            {/* Tab Navigation - Geometric Style */}
            <div className="flex space-x-2 mb-4 overflow-x-auto hide-scrollbar">
              {menuItems.map((item) => (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-xl font-medium transition-all whitespace-nowrap text-xs border-2 flex-shrink-0 ${
                    location.pathname === item.path
                      ? 'border-primary-500 bg-primary-500 text-white shadow-geometric'
                      : 'border-neutral-200 bg-white text-neutral-700 hover:border-primary-300 hover:bg-primary-50'
                  }`}
                >
                  <SafeIcon icon={item.icon} className="w-3 h-3 flex-shrink-0" />
                  <span className="text-ellipsis">{item.label}</span>
                </Link>
              ))}
            </div>
          </motion.div>

          {/* Quick Actions - Geometric Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-4 border-2 border-neutral-200 shadow-geometric-lg"
          >
            <div className="flex justify-between items-center mb-4">
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold text-neutral-800 flex items-center">
                  <SafeIcon icon={FiGrid} className="w-5 h-5 mr-2 text-primary-600 flex-shrink-0" />
                  <span className="text-ellipsis">Quick Actions</span>
                </h2>
                <p className="text-xs text-neutral-500 mt-1 text-wrap">Access key features instantly</p>
              </div>
              <button
                onClick={toggleQuickActions}
                className="quick-actions-toggle flex items-center space-x-1 p-2 bg-neutral-100 rounded-xl hover:bg-neutral-200 transition-colors border border-neutral-200 flex-shrink-0"
              >
                <span className="text-xs text-neutral-600 font-medium"> {quickActions.length - 2} more </span>
                <SafeIcon icon={showQuickActions ? FiChevronUp : FiChevronDown} className="w-3 h-3 text-neutral-600" />
              </button>
            </div>
            {/* Featured Quick Actions */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              {quickActions.slice(0, 2).map((action, index) => (
                <Link
                  key={index}
                  to={action.path}
                  className="flex items-center space-x-2 p-3 rounded-xl bg-gradient-to-br from-neutral-50 to-white border-2 border-neutral-200 hover:border-primary-300 hover:shadow-geometric transition-all group"
                >
                  <div
                    className={`p-2 rounded-xl bg-geometric-${action.color}/10 group-hover:bg-geometric-${action.color}/20 transition-colors flex-shrink-0`}
                  >
                    <SafeIcon icon={action.icon} className={`w-4 h-4 text-geometric-${action.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-neutral-700 group-hover:text-neutral-800 text-sm text-ellipsis block">
                      {action.name}
                    </span>
                    <SafeIcon icon={FiArrowRight} className="w-3 h-3 text-neutral-400 group-hover:text-primary-500 transition-colors" />
                  </div>
                </Link>
              ))}
            </div>
            {/* Dropdown for Additional Actions */}
            <AnimatePresence>
              {showQuickActions && (
                <motion.div
                  id="quick-actions-dropdown"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="grid grid-cols-2 gap-3 overflow-hidden"
                >
                  {quickActions.slice(2).map((action, index) => (
                    <Link
                      key={index}
                      to={action.path}
                      className="flex items-center space-x-2 p-3 rounded-xl bg-gradient-to-br from-neutral-50 to-white border-2 border-neutral-200 hover:border-primary-300 hover:shadow-geometric transition-all group"
                    >
                      <div
                        className={`p-2 rounded-xl bg-geometric-${action.color}/10 group-hover:bg-geometric-${action.color}/20 transition-colors flex-shrink-0`}
                      >
                        <SafeIcon icon={action.icon} className={`w-4 h-4 text-geometric-${action.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-neutral-700 group-hover:text-neutral-800 text-sm text-ellipsis block">
                          {action.name}
                        </span>
                        <SafeIcon
                          icon={FiArrowRight}
                          className="w-3 h-3 text-neutral-400 group-hover:text-primary-500 transition-colors"
                        />
                      </div>
                    </Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Study Subjects - Geometric Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-2xl p-4 border-2 border-neutral-200 shadow-geometric-lg"
          >
            <h2 className="text-lg font-bold text-neutral-800 mb-4 flex items-center">
              <SafeIcon icon={FiBookOpen} className="w-5 h-5 mr-2 text-primary-600 flex-shrink-0" />
              <span className="text-ellipsis">Study Subjects</span>
            </h2>
            <div className="grid grid-cols-2 gap-3">
              {studySubjects.map((subject, index) => (
                <Link
                  key={index}
                  to={subject.path}
                  className="flex items-center space-x-2 p-3 rounded-xl bg-gradient-to-br from-neutral-50 to-white border-2 border-neutral-200 hover:border-primary-300 hover:shadow-geometric transition-all group relative overflow-hidden"
                >
                  {/* Geometric decoration */}
                  <div className="absolute top-1 right-1 opacity-5 group-hover:opacity-10 transition-opacity">
                    <Hexagon size="md" color={subject.color} />
                  </div>
                  <div
                    className={`p-2 rounded-xl bg-geometric-${subject.color}/10 group-hover:bg-geometric-${subject.color}/20 transition-colors relative z-10 flex-shrink-0`}
                  >
                    <SafeIcon icon={subject.icon} className={`w-4 h-4 text-geometric-${subject.color}`} />
                  </div>
                  <div className="flex-1 min-w-0 relative z-10">
                    <span className="font-medium text-neutral-700 group-hover:text-neutral-800 text-sm block text-ellipsis">
                      {subject.name}
                    </span>
                    <SafeIcon icon={FiPlay} className="w-2 h-2 text-neutral-400 group-hover:text-primary-500 mt-1 transition-colors" />
                  </div>
                </Link>
              ))}
            </div>
          </motion.div>

          {/* Achievement Section - Geometric Style */}
          <GeometricBackground pattern="diagonal">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl p-4 text-white shadow-geometric-xl relative overflow-hidden"
            >
              {/* Geometric decorations */}
              <div className="absolute top-4 right-4">
                <Triangle size="xl" color="primary" className="text-white/10" />
              </div>
              <div className="absolute bottom-4 left-4">
                <Circle size="lg" color="primary" className="text-white/10" />
              </div>
              <div className="text-center relative z-10">
                <SafeIcon icon={FiAward} className="w-10 h-10 mx-auto mb-3 animate-geometric-pulse" />
                <h2 className="text-xl font-bold mb-2 text-wrap">Your Progress</h2>
                <p className="text-primary-100 mb-4 text-sm text-wrap">
                  Keep up the great work! You're making excellent progress on your learning journey.
                </p>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: userStats.totalPoints, label: 'Total Points' },
                    { value: userStats.quizzesCompleted, label: 'Quizzes Done' },
                    { value: userStats.studyGroups, label: 'Study Groups' },
                  ].map((item, index) => (
                    <div key={index} className="bg-white/20 backdrop-blur-sm rounded-xl p-3 border border-white/10">
                      <div className="text-lg font-bold">{item.value}</div>
                      <div className="text-xs text-primary-100 text-wrap">{item.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </GeometricBackground>
        </div>
      );
    };

    export default Home;